import ProjectOverview, {
  isMissingReports,
  isProjectFinished,
  isProjectOverviewWebsiteOnly,
} from "@/data/models/ProjectOverview";
import User from "@/data/models/User";
import { UserLookup } from "@/data/models/UserLookup";
import {
  getLastestProjectState,
  ProjectStateInfo,
  ProjectStateType,
  ProjectApprovalDoneInfo,
  ProjectFinishedInfo,
  ProjectPhase,
  AppointmentStepInfo,
  AppointmentType,
  InsuranceOfferResultInfo,
  DamageAssessmentDoneInfo,
} from "@/data/models/ProjectState";
import {
  isDryingProject,
  isLeakDetectionProject,
} from "@/data/repository/ProjectRepository";
import {
  DryingProjectOverview,
  DamagePatternConclusionState,
  NextDryingAction,
  calculateDryingProgressPercent,
} from "@/data/models/DryingProjectOverview";
import {
  RepairAction,
  containsLeakDetectionInfo,
  isFlexProject,
} from "@/data/models/ProjectDetails";
import { getProjectStepContainer } from "@/data/models/ProjectSteps";
import { DateTime } from "luxon";
import { getProjectSupervisorSource } from "@/data/repository/ClientRepository";
import { ProjectSupervisorSource } from "@/data/models/Settings";
import { damageAssessmentAndLeakDetectionProjectsAccess, dryingAndRepairProjectsAccess, dryingProjectsAccess, leakDetectionProjectsAccess, managerProjectsAccess, officeAccess, projectManagementAccess } from "@/permissions/UserPermissions";

export enum ProjectStateFilterType {
  NEW_PROJECT = "NEW_PROJECT",
  LEAK_DETECTION = "LEAK_DETECTION",
  ALL_PROJECTS = "ALL_PROJECTS",
  OVERALL = "OVERALL",
  WITH_HY_DRY_DEVICES = "WITH_HY_DRY_DEVICES",
  DAMAGE_ASSESSMENT = "DAMAGE_ASSESSMENT",
  DRYING = "DRYING",
  REPAIR = "REPAIR",

  RELEVANT_NEW_PROJECTS = "NEW_PROJECT_RELEVANT",

  MISSING_DRYING_APPROVAL_PROJECTORGANISATION = "MISSING_DRYING_APPROVAL_PROJECTORGANISATION",
  MISSING_PROJECT_CONCLUSION_PROJECTORGANISATION = "MISSING_PROJECT_CONCLUSION_PROJECTORGANISATION",

  MISSING_PROJECT_APPROVAL = "MISSING_PROJECT_APPROVAL",
  MISSING_FIRST_CONTACT = "MISSING_FIRST_CONTACT",
  INSURANCE_OFFER = "INSURANCE_OFFER",
  MISSING_DRYING_APPROVAL = "MISSING_DRYING_APPROVAL",
  MISSING_PROJECT_CONCLUSION = "MISSING_PROJECT_CONCLUSION",
  MISSING_PROJECT_CONCLUSION_NO_APP = "MISSING_PROJECT_CONCLUSION_NO_APP",

  MAKE_LEAK_DETECTION_APPOINTMENT = "MAKE_LEAK_DETECTION_APPOINTMENT",
  MAKE_DAMAGE_ASSESSMENT_APPOINTMENT = "MAKE_DAMAGE_ASSESSMENT_APPOINTMENT",
  MAKE_SETUP_DRYING_APPOINTMENT = "MAKE_SETUP_DRYING_APPOINTMENT",
  MAKE_INTERMEDIATE_MEASUREMENT_APPOINTMENT = "MAKE_INTERMEDIATE_MEASUREMENT_APPOINTMENT",
  MAKE_DRYING_CONCLUSION_APPOINTMENT = "MAKE_DRYING_CONCLUSION_APPOINTMENT",
  MAKE_REPAIR_APPOINTMENT = "MAKE_REPAIR_APPOINTMENT",

  MISSING_PROJECT_OWNER = "MISSING_PROJECT_OWNER",

  PROJECT_OWNER = "PORJECT_OWNER",
  PROJECT_SUPERVISOR = "PROJECT_SUPERVISOR",
  FOLLOW_UP_INSPECTION = "FOLLOW_UP_INSPECTION",
  OVERDUE = "OVERDUE",

  RECENTLY_FINISHED = "RECENTLY_FINISHED",
  MISSING_REPORT = "MISSING_REPORT",

  ASSIGN_DEVICES = "ASSIGN_DEVICES",

  OVERDUE_RELEVANT_PROJECTS = "OVERDUE_RELEVANT_PROJECTS",
  OVERDUE_MY_PROJECTS = "OVERDUE_MY_PROJECTS",

  RECENTLY_FINISHED_RELEVANT_PROJECTS = "RECENTLY_FINISHED_RELEVANT_PROJECTS",
}

export enum CurrentUserRequirement {
  HAS_OFFICE,
  HAS_PROJECT_MANAGEMENT,
  HAS_DAMAGE_ASSESSMENT_AND_LEAK_DETECTION,
  HAS_DRYING_AND_REPAIR,
  HAS_MISSING_PROJECT_SUPERVISOR,
  IS_OPEN_AND_HAS_OFFICE,
  IS_OPEN_AND_HAS_PROJECT_MANAGEMENT,
  IS_PROJECT_OWNER,
  IS_PROJECT_SUPERVISOR,
  IS_PROJECT_SUPERVISOR_AND_NOT_OWNER,
  IS_MANAGER,
  IS_USER_SUPERVISOR,
}

export interface ProjectFilterOptions {
  lastCompletedStep?: ProjectStateInfo | null;
  ignoreUserRequirements?: boolean;
  log?: boolean;
}

function isUserAllowedToSeeProject(
  targetUser: User,
  projectOverview: ProjectOverview
): boolean {

  if (targetUser.id == projectOverview.projectOwner) {
    return true;
  }

  if (projectOverview.projectOwner) {
    return false;
  }

  if (
    isLeakDetectionProject(projectOverview.id) ||
    containsLeakDetectionInfo(projectOverview)
  ) {
    if (!leakDetectionProjectsAccess(targetUser)) {
      return false;
    }
  }

  if (!leakDetectionProjectsAccess(targetUser)) {
    if (
      isLeakDetectionProject(projectOverview.id) ||
      containsLeakDetectionInfo(projectOverview)
    ) {
      return false;
    }

    const projectSteps = getProjectStepContainer(projectOverview);

    if (!projectSteps) {
      return false;
    }

    if (!projectSteps.leakDetectionResult) {
      return false; 
    }
  }

  if (isDryingProject(projectOverview.id)) {
    if (!dryingProjectsAccess(targetUser)) {
      return false;
    }

    const dryingProjectOverview = projectOverview as DryingProjectOverview;

    if (!dryingProjectOverview) {
      return false;
    }

    return (
      dryingProjectOverview.damagePatternConclusionComplete === false ||
      dryingProjectOverview.damagePatternConclusionState !==
        DamagePatternConclusionState.CARRY_OUT_DRYING
    );
  }

  return true;
}

export function doesTargetUserMatchUserRequirementForProject(
  userRequirement: CurrentUserRequirement,
  targetUser: User | null,
  projectOverview: ProjectOverview,
  userLookup: UserLookup
): boolean | undefined {
  if (targetUser == null) {
    return true;
  }

  switch (userRequirement) {
    case CurrentUserRequirement.HAS_MISSING_PROJECT_SUPERVISOR: {
      if (!officeAccess(targetUser)) {
        return false;
      }

      return (
        !projectOverview.projectSupervisorId &&
        officeAccess(targetUser)
      );
    }

    case CurrentUserRequirement.HAS_OFFICE: {
      if (officeAccess(targetUser)) {
        return isUserAllowedToSeeProject(targetUser, projectOverview);
      }

      return false;
    }

    case CurrentUserRequirement.HAS_PROJECT_MANAGEMENT: {
      if (projectManagementAccess(targetUser)) {
        return isUserAllowedToSeeProject(targetUser, projectOverview);
      }

      return false;
    }

    case CurrentUserRequirement.HAS_DAMAGE_ASSESSMENT_AND_LEAK_DETECTION: {
      if (damageAssessmentAndLeakDetectionProjectsAccess(targetUser)) {
        return isUserAllowedToSeeProject(targetUser, projectOverview);
      }

      return false;
    }

    case CurrentUserRequirement.HAS_DRYING_AND_REPAIR: {
      if (dryingAndRepairProjectsAccess(targetUser)) {
        return isUserAllowedToSeeProject(targetUser, projectOverview);
      }

      return false;
    }

    case CurrentUserRequirement.IS_OPEN_AND_HAS_OFFICE: {
      if (!projectOverview.projectOwner) {
        if (officeAccess(targetUser)) {
          return isUserAllowedToSeeProject(targetUser, projectOverview);
        }
      }

      return false;
    }

    case CurrentUserRequirement.IS_OPEN_AND_HAS_PROJECT_MANAGEMENT: {
      if (!projectOverview.projectOwner) {
        if (projectManagementAccess(targetUser)) {
          return isUserAllowedToSeeProject(targetUser, projectOverview);
        }
      }

      return false;
    }

    case CurrentUserRequirement.IS_USER_SUPERVISOR: {
      const isSupervisorOfProjectOwner = userLookup.isUserSupervisorOfTarget(
        targetUser,
        projectOverview.projectOwner
      );

      const isSupervisorOfProjectSupervisor = userLookup.isUserSupervisorOfTarget(
        targetUser,
        projectOverview.projectSupervisorId
      );

      return isSupervisorOfProjectOwner || isSupervisorOfProjectSupervisor;
    }

    case CurrentUserRequirement.IS_PROJECT_OWNER: {
      return projectOverview.projectOwner == targetUser.id;
    }

    case CurrentUserRequirement.IS_PROJECT_SUPERVISOR: {
      return projectOverview.projectSupervisorId == targetUser.id;
    }

    case CurrentUserRequirement.IS_PROJECT_SUPERVISOR_AND_NOT_OWNER: {
      return (
        projectOverview.projectSupervisorId == targetUser.id &&
        projectOverview.projectOwner != targetUser.id
      );
    }

    case CurrentUserRequirement.IS_MANAGER: {
      const matchesUserRoleRequirements = managerProjectsAccess(targetUser);

      if (projectOverview.projectOwner != null) {
        if (projectOverview.projectSupervisorId == null) {
          return matchesUserRoleRequirements;
        } else {
          return false;
        }
      } else {
        return matchesUserRoleRequirements;
      }
    }
  }
}

export function isAppointmentStepInfoComplete(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: any
) {
  if (!step) {
    return false;
  }

  if (step.completed) {
    return true;
  }

  return step.appointmentDate && step.responsibleUser;
}

function matchesDryingConclusionAppointmentFilter(
  dryingProjectOverview: DryingProjectOverview
) {
  const projectStepsContainer = dryingProjectOverview.dryingProjectSteps;

  if (!projectStepsContainer) {
    return false;
  }

  const doesAppointmentWithMissingInfoExist = projectStepsContainer.dryingConclusionAppointments
    ? projectStepsContainer.dryingConclusionAppointments.some(
        (appointmentInfo) => !isAppointmentStepInfoComplete(appointmentInfo)
      )
    : false;

  const needsToCreateAppointment =
    dryingProjectOverview.nextDryingAction ===
      NextDryingAction.DRYING_CONCLUSION &&
    (projectStepsContainer.dryingConclusionAppointments
      ? !projectStepsContainer.dryingConclusionAppointments.some(
          (appointmentInfo) => isAppointmentStepInfoComplete(appointmentInfo)
        )
      : true);

  return (
    !dryingProjectOverview.devicePackupComplete &&
    isFlexProject(dryingProjectOverview) &&
    (doesAppointmentWithMissingInfoExist || needsToCreateAppointment)
  );
}

function matchesIntermediateMeasurementAppointmentFilter(
  dryingProjectOverview: DryingProjectOverview
) {
  const projectStepsContainer = dryingProjectOverview.dryingProjectSteps;

  if (!projectStepsContainer) {
    return false;
  }

  const doesAppointmentWithMissingInfoExist = projectStepsContainer.intermediateMeasurementAppointments
    ? projectStepsContainer.intermediateMeasurementAppointments.some(
        (appointmentInfo) => !isAppointmentStepInfoComplete(appointmentInfo)
      )
    : false;

  const needsToCreateAppointment =
    dryingProjectOverview.nextDryingAction === NextDryingAction.MEASUREMENT &&
    (projectStepsContainer.intermediateMeasurementAppointments
      ? !projectStepsContainer.intermediateMeasurementAppointments.some(
          (appointmentInfo) => isAppointmentStepInfoComplete(appointmentInfo)
        )
      : true);

  return (
    !dryingProjectOverview.isFinished &&
    isFlexProject(dryingProjectOverview) &&
    (doesAppointmentWithMissingInfoExist || needsToCreateAppointment)
  );
}

export class ProjectStateFilter {
  userRequirements: CurrentUserRequirement[];
  filterType: ProjectStateFilterType;

  constructor(
    filterType: ProjectStateFilterType,
    userRequirements?: CurrentUserRequirement[]
  ) {
    this.filterType = filterType;

    if (userRequirements) {
      this.userRequirements = userRequirements;
    } else {
      switch (filterType) {
        case ProjectStateFilterType.MISSING_PROJECT_CONCLUSION_NO_APP:
        case ProjectStateFilterType.WITH_HY_DRY_DEVICES:
        case ProjectStateFilterType.OVERDUE:
        case ProjectStateFilterType.NEW_PROJECT:
        case ProjectStateFilterType.ALL_PROJECTS: {
          this.userRequirements = [];
          break;
        }

        // Meine Projekte
        case ProjectStateFilterType.PROJECT_OWNER: {
          this.userRequirements = [
            CurrentUserRequirement.IS_PROJECT_OWNER,
          ];
          break;
        }
        case ProjectStateFilterType.PROJECT_SUPERVISOR: {
          this.userRequirements = [
            CurrentUserRequirement.IS_PROJECT_SUPERVISOR_AND_NOT_OWNER,
          ];
          break;
        }
        case ProjectStateFilterType.MISSING_PROJECT_OWNER:
        case ProjectStateFilterType.FOLLOW_UP_INSPECTION:
        case ProjectStateFilterType.OVERDUE_MY_PROJECTS:
        case ProjectStateFilterType.RECENTLY_FINISHED:
        case ProjectStateFilterType.MISSING_REPORT: {
          this.userRequirements = [
            CurrentUserRequirement.IS_PROJECT_SUPERVISOR,
          ];
          break;
        }

        // Terminvereinbarungen
        case ProjectStateFilterType.MAKE_REPAIR_APPOINTMENT:
        case ProjectStateFilterType.MAKE_SETUP_DRYING_APPOINTMENT: {
          this.userRequirements = [
            CurrentUserRequirement.HAS_OFFICE,
            CurrentUserRequirement.IS_PROJECT_SUPERVISOR,
            CurrentUserRequirement.IS_PROJECT_OWNER,
          ];
          break;
        }
        case ProjectStateFilterType.MAKE_DRYING_CONCLUSION_APPOINTMENT:
        case ProjectStateFilterType.MAKE_INTERMEDIATE_MEASUREMENT_APPOINTMENT: {
          this.userRequirements = [
            CurrentUserRequirement.HAS_OFFICE,
            CurrentUserRequirement.HAS_PROJECT_MANAGEMENT,
            CurrentUserRequirement.IS_PROJECT_SUPERVISOR,
            CurrentUserRequirement.IS_PROJECT_OWNER,
          ];
          break;
        }
        case ProjectStateFilterType.MAKE_DAMAGE_ASSESSMENT_APPOINTMENT:
        case ProjectStateFilterType.MAKE_LEAK_DETECTION_APPOINTMENT: {
          this.userRequirements = [
            CurrentUserRequirement.HAS_OFFICE,
            CurrentUserRequirement.IS_PROJECT_SUPERVISOR,
          ];
          break;
        }

        // Relevante Projekte
        case ProjectStateFilterType.OVERALL:
        case ProjectStateFilterType.RELEVANT_NEW_PROJECTS:
        case ProjectStateFilterType.LEAK_DETECTION:
        case ProjectStateFilterType.DAMAGE_ASSESSMENT:
        case ProjectStateFilterType.MISSING_DRYING_APPROVAL:
        case ProjectStateFilterType.DRYING:
        case ProjectStateFilterType.REPAIR:
        case ProjectStateFilterType.OVERDUE_RELEVANT_PROJECTS:
        case ProjectStateFilterType.RECENTLY_FINISHED_RELEVANT_PROJECTS: {
          this.userRequirements = [
            CurrentUserRequirement.IS_OPEN_AND_HAS_OFFICE,
            CurrentUserRequirement.IS_OPEN_AND_HAS_PROJECT_MANAGEMENT,
            CurrentUserRequirement.IS_PROJECT_OWNER,
            CurrentUserRequirement.IS_PROJECT_SUPERVISOR,
          ];

          if (getProjectSupervisorSource() === ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER) {
            this.userRequirements.push(
              CurrentUserRequirement.IS_USER_SUPERVISOR,
            )
          }
          break;
        }
        // Projektorganisation
        case ProjectStateFilterType.MISSING_PROJECT_APPROVAL: {
          this.userRequirements = [
            CurrentUserRequirement.HAS_OFFICE,
          ];
          break;
        }
        case ProjectStateFilterType.ASSIGN_DEVICES: {
          this.userRequirements = [
            CurrentUserRequirement.HAS_OFFICE,
          ];
          break;
        }
        case ProjectStateFilterType.MISSING_FIRST_CONTACT: {
          this.userRequirements = [
            CurrentUserRequirement.HAS_OFFICE,
            CurrentUserRequirement.HAS_PROJECT_MANAGEMENT,
          ];
          break;
        }
        case ProjectStateFilterType.INSURANCE_OFFER:
        case ProjectStateFilterType.MISSING_DRYING_APPROVAL_PROJECTORGANISATION:
        case ProjectStateFilterType.MISSING_PROJECT_CONCLUSION_PROJECTORGANISATION: {
          this.userRequirements = [
            CurrentUserRequirement.HAS_OFFICE,
            CurrentUserRequirement.IS_PROJECT_SUPERVISOR,
          ];
          break;
        }

        default: {
          this.userRequirements = [];
          break;
        }
      }
    }
  }

  /**
   * True when this filter accepts every project regardless of its content, so
   * that callers can skip `matchesProjectOverview` for the whole list. Only
   * the filter type and the user requirements decide this, never a project.
   *
   * Worth checking before iterating large lists: `matchesProjectOverview`
   * resolves the latest project state, which walks all project steps, and the
   * archive can hold more than ten thousand projects.
   */
  matchesEveryProject(filterOptions?: ProjectFilterOptions) {
    const matchesEveryProjectState =
      this.filterType === ProjectStateFilterType.OVERALL ||
      this.filterType === ProjectStateFilterType.ALL_PROJECTS;

    if (!matchesEveryProjectState) {
      return false;
    }

    return (
      filterOptions?.ignoreUserRequirements === true ||
      this.userRequirements.length === 0
    );
  }

  matchesProjectOverview(
    projectOverview: ProjectOverview,
    targetUser: User | null,
    userLookup: UserLookup,
    today: Date,
    filterOptions?: ProjectFilterOptions
  ) {
    let matchesUserRoleRequirements = true;

    if (this.userRequirements.length > 0) {
      matchesUserRoleRequirements = this.userRequirements
        .map((requirement) => {
          const result = doesTargetUserMatchUserRequirementForProject(
            requirement,
            targetUser,
            projectOverview,
            userLookup
          );

          return result;
        })
        .some((value) => value);
    }

    if (
      !filterOptions?.ignoreUserRequirements &&
      !matchesUserRoleRequirements
    ) {
      return false;
    }

    const latestStep =
      filterOptions?.lastCompletedStep ??
      getLastestProjectState(projectOverview, today);

    switch (this.filterType) {
      case ProjectStateFilterType.MISSING_REPORT: {
        return isMissingReports(projectOverview);
      }

      case ProjectStateFilterType.OVERALL:
      case ProjectStateFilterType.ALL_PROJECTS: {
        return true;
      }

      case ProjectStateFilterType.WITH_HY_DRY_DEVICES: {
        if (isLeakDetectionProject(projectOverview.id)) {
          return false;
        }

        const dryingProjectOverview = projectOverview as DryingProjectOverview;

        return (
          dryingProjectOverview.assignedPanels &&
          dryingProjectOverview.assignedPanels.length > 0
        );
      }

      case ProjectStateFilterType.MISSING_PROJECT_CONCLUSION_NO_APP: {
        if (isLeakDetectionProject(projectOverview.id)) {
          return false;
        }

        if (isProjectFinished(projectOverview)) {
          return false;
        }

        const dryingProjectOverview = projectOverview as DryingProjectOverview;

        const startDate = dryingProjectOverview.startDate;

        if (!startDate) {
          return false;
        }

        const now = DateTime.fromJSDate(new Date());
        const hasReachedProjectLengthThreshold =
          Math.abs(
            now.diff(DateTime.fromJSDate(startDate.toDate()), "days").days
          ) >= 14;

        const assignedPanels = dryingProjectOverview.assignedPanels;

        return (
          hasReachedProjectLengthThreshold &&
          (!assignedPanels || assignedPanels.length === 0)
        );
      }

      case ProjectStateFilterType.MISSING_PROJECT_APPROVAL: {
        return latestStep?.projectStepType === ProjectStateType.ApproveProject;
      }

      case ProjectStateFilterType.INSURANCE_OFFER: {
        if (
          isProjectOverviewWebsiteOnly(projectOverview)
        ) {
          return false;
        }

        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.InsuranceOfferSend: {
              return true;
            }
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.MISSING_FIRST_CONTACT: {
        if (
          isProjectOverviewWebsiteOnly(projectOverview)
        ) {
          return false;
        }

        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.ProjectApprovalDone: {
              return (latestStep as ProjectApprovalDoneInfo).wasApproved;
            }
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.ASSIGN_DEVICES: {
        if (isProjectOverviewWebsiteOnly(projectOverview)) {
          if (latestStep) {
            return latestStep.projectStepType === ProjectStateType.AssignDevices;
          }

          return false;
        }

        return false;
      }

      case ProjectStateFilterType.MISSING_PROJECT_OWNER: {
        return !projectOverview.projectOwner;
      }

      case ProjectStateFilterType.REPAIR: {
        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.RepairStarted: {
              return true;
            }
            case ProjectStateType.Appointment: {
              const appointmentType = (latestStep as AppointmentStepInfo)
                .appointmentType;

              return appointmentType === AppointmentType.REPAIR;
            }
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.DRYING: {
        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.InsuranceOfferResult: {
              return (latestStep as InsuranceOfferResultInfo).dryingApproved;
            }
            case ProjectStateType.DryingApproved: {
              return true;
            }
            case ProjectStateType.DryingStarted: {
              return true;
            }
            case ProjectStateType.DryingResult: {
              return true;
            }
            case ProjectStateType.Appointment: {
              const appointmentType = (latestStep as AppointmentStepInfo)
                .appointmentType;

              return (
                appointmentType != AppointmentType.DAMAGE_ASSESSMENT &&
                appointmentType != AppointmentType.LEAK_DETECTION &&
                appointmentType != AppointmentType.REPAIR
              );
            }
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.MISSING_DRYING_APPROVAL_PROJECTORGANISATION:
      case ProjectStateFilterType.MISSING_DRYING_APPROVAL: {
        if (
          isProjectOverviewWebsiteOnly(projectOverview)
        ) {
          return false;
        }

        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.InsuranceOfferSend: {
              return true;
            }
            case ProjectStateType.DamageAssessmentDone: {
              return (latestStep as DamageAssessmentDoneInfo).isProjectOnHold;
            }
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.DAMAGE_ASSESSMENT: {
        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.DamageAssessmentRunning: {
              return true;
            }
            case ProjectStateType.ProjectFinished: {
              return (
                (latestStep as ProjectFinishedInfo).projectPhase ===
                ProjectPhase.DamageAssessment
              );
            }
            case ProjectStateType.DamageAssessmentDone: {
              return (latestStep as DamageAssessmentDoneInfo).isProjectOnHold;
            }
            case ProjectStateType.InsuranceOfferResult: {
              return (latestStep as InsuranceOfferResultInfo).projectFinished;
            }
            case ProjectStateType.Appointment:
              return (
                (latestStep as AppointmentStepInfo).appointmentType ===
                AppointmentType.DAMAGE_ASSESSMENT
              );
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.MISSING_PROJECT_CONCLUSION_PROJECTORGANISATION:
      case ProjectStateFilterType.MISSING_PROJECT_CONCLUSION: {
        if (
          isProjectOverviewWebsiteOnly(projectOverview)
        ) {
          return (
            latestStep?.projectStepType === ProjectStateType.ConcludeProject
          );
        }

        if (isDryingProject(projectOverview.id)) {
          const dryingProjectOverview = projectOverview as DryingProjectOverview;

          if (!dryingProjectOverview.dryingEnd) {
            return false;
          }

          if (dryingProjectOverview.projectConclusionComplete) {
            return false;
          }

          if (
            !dryingProjectOverview.repairAction ||
            dryingProjectOverview.repairAction == RepairAction.NOT_NEEDED ||
            !!dryingProjectOverview.repairCompletedBy
          ) {
            return true;
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.LEAK_DETECTION: {
        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.LeakDetectionRunning: {
              return true;
            }
            case ProjectStateType.ProjectFinished: {
              return (
                (latestStep as ProjectFinishedInfo).projectPhase ===
                ProjectPhase.LeakDetection
              );
            }
            case ProjectStateType.Appointment:
              return (
                (latestStep as AppointmentStepInfo).appointmentType ===
                AppointmentType.LEAK_DETECTION
              );
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.RELEVANT_NEW_PROJECTS:
      case ProjectStateFilterType.NEW_PROJECT: {
        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.ProjectApprovalDone: {
              const projectApprovalInfo = latestStep as ProjectApprovalDoneInfo;
              return projectApprovalInfo.wasApproved;
            }
            case ProjectStateType.FirstContactDone:
              return true;
            case ProjectStateType.ApproveProject:
              return true;
            case ProjectStateType.SetProjectSupervisor:
              return true;
          }

          return false;
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.MAKE_LEAK_DETECTION_APPOINTMENT: {
        if (!containsLeakDetectionInfo(projectOverview)) {
          return false;
        }

        const projectSteps = getProjectStepContainer(projectOverview);

        if (!projectSteps) {
          return false;
        }

        if (isAppointmentStepInfoComplete(projectSteps.leakDetectionResult)) {
          return false;
        }

        if (latestStep) {
          switch (latestStep.projectStepType) {
            case ProjectStateType.FirstContactDone:
            case ProjectStateType.ProjectApprovalDone:
              return true;
            
            case ProjectStateType.Appointment: {
              const appointmentInfo = latestStep as AppointmentStepInfo;
              return (
                appointmentInfo.appointmentType ===
                  AppointmentType.LEAK_DETECTION &&
                !isAppointmentStepInfoComplete(appointmentInfo)
              );
            }
            default:
              return false;
          }
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.MAKE_DAMAGE_ASSESSMENT_APPOINTMENT: {
        if (!isFlexProject(projectOverview)) {
          return false;
        }

        if (!isDryingProject(projectOverview.id)) {
          return false;
        }

        const dryingProjectOverview = projectOverview as DryingProjectOverview;

        if (dryingProjectOverview.damagePatternComplete === true) {
          return false;
        }

        const projectSteps = getProjectStepContainer(projectOverview);

        if (!projectSteps) {
          return false;
        }

        if (
          isAppointmentStepInfoComplete(
            projectSteps.damageAssessmentAppointment
          )
        ) {
          return false;
        }

        if (latestStep) {
          if (latestStep.projectStepType === ProjectStateType.Appointment) {
            const appointmentInfo = latestStep as AppointmentStepInfo;
            return (
              appointmentInfo.appointmentType ===
                AppointmentType.DAMAGE_ASSESSMENT &&
              !isAppointmentStepInfoComplete(appointmentInfo)
            );
          } else if (containsLeakDetectionInfo(projectOverview)) {
            return (
              latestStep.projectStepType ===
              ProjectStateType.LeakDetectionFinished
            );
          } else {
            return (
              latestStep.projectStepType === ProjectStateType.FirstContactDone ||
              (
                latestStep.projectStepType === ProjectStateType.ApproveProject &&
                !isProjectOverviewWebsiteOnly(projectOverview)
              )
            );
          }
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.MAKE_SETUP_DRYING_APPOINTMENT: {
        if (!isFlexProject(projectOverview)) {
          return false;
        }

        if (!isDryingProject(projectOverview.id)) {
          return false;
        }

        const dryingProjectOverview = projectOverview as DryingProjectOverview;

        if (
          !dryingProjectOverview.damagePatternConclusionComplete ||
          dryingProjectOverview.damagePatternConclusionState !==
            DamagePatternConclusionState.CARRY_OUT_DRYING
        ) {
          return false;
        }

        if (dryingProjectOverview.dryingSetupComplete) {
          return false;
        }

        const projectSteps = getProjectStepContainer(projectOverview);

        if (!projectSteps) {
          return false;
        }

        if (!projectSteps.dryingSetupAppointments) {
          return true;
        }

        return !projectSteps.dryingSetupAppointments.some((appointmentInfo) =>
          isAppointmentStepInfoComplete(appointmentInfo)
        );
      }

      case ProjectStateFilterType.MAKE_INTERMEDIATE_MEASUREMENT_APPOINTMENT: {
        if (!latestStep) {
          return false;
        }

        if (!isFlexProject(projectOverview)) {
          return false;
        }

        if (!isDryingProject(projectOverview.id)) {
          return false;
        }

        if (latestStep.projectStepType == ProjectStateType.Appointment) {
          const appointmentInfo = latestStep as AppointmentStepInfo;

          if (
            appointmentInfo.appointmentType ===
              AppointmentType.INTERMEDIATE_MEASUREMENT &&
            !isAppointmentStepInfoComplete(appointmentInfo)
          ) {
            return true;
          }
        }

        const dryingProjectOverview = projectOverview as DryingProjectOverview;

        return (
          latestStep.projectStepType === ProjectStateType.DryingStarted &&
          matchesIntermediateMeasurementAppointmentFilter(dryingProjectOverview)
        );
      }

      case ProjectStateFilterType.MAKE_DRYING_CONCLUSION_APPOINTMENT: {
        if (!latestStep) {
          return false;
        }

        if (!isDryingProject(projectOverview.id)) {
          return false;
        }

        if (latestStep.projectStepType == ProjectStateType.Appointment) {
          const appointmentInfo = latestStep as AppointmentStepInfo;

          if (
            appointmentInfo.appointmentType ===
              AppointmentType.DRYING_CONCLUSION &&
            !isAppointmentStepInfoComplete(appointmentInfo)
          ) {
            return true;
          }
        }

        const dryingProjectOverview = projectOverview as DryingProjectOverview;

        return (
          latestStep.projectStepType === ProjectStateType.DryingStarted &&
          matchesDryingConclusionAppointmentFilter(dryingProjectOverview)
        );
      }

      case ProjectStateFilterType.MAKE_REPAIR_APPOINTMENT: {
        if (!latestStep) {
          return false;
        }

        if (!isDryingProject(projectOverview.id)) {
          return false;
        }

        const dryingProjectOverview = projectOverview as DryingProjectOverview;

        if (dryingProjectOverview.repairAction !== RepairAction.APPOINTMENT) {
          return false;
        }

        const projectStepsContainer = getProjectStepContainer(projectOverview);

        return projectStepsContainer?.repairAppointments
          ? !projectStepsContainer.repairAppointments.some((appointmentInfo) =>
              isAppointmentStepInfoComplete(appointmentInfo)
            )
          : false;
      }

      case ProjectStateFilterType.PROJECT_OWNER: {
        return targetUser && projectOverview.projectOwner === targetUser.id;
      }

      case ProjectStateFilterType.PROJECT_SUPERVISOR: {
        return (
          targetUser &&
          projectOverview.projectOwner !== targetUser.id &&
          projectOverview.projectSupervisorId === targetUser.id
        );
      }

      case ProjectStateFilterType.FOLLOW_UP_INSPECTION: {
        if (!latestStep) {
          return false;
        }

        if (latestStep.projectStepType === ProjectStateType.Appointment) {
          const appointmentInfo = latestStep as AppointmentStepInfo;

          return (
            appointmentInfo.appointmentType ===
              AppointmentType.FOLLOW_UP_INSPECTION &&
            appointmentInfo.appointmentDate &&
            appointmentInfo.appointmentDate.getTime() <= today.getTime()
          );
        } else {
          return false;
        }
      }

      case ProjectStateFilterType.OVERDUE_RELEVANT_PROJECTS:
      case ProjectStateFilterType.OVERDUE_MY_PROJECTS:
      case ProjectStateFilterType.OVERDUE: {
        if (!latestStep) {
          return false;
        }

        if (!isDryingProject(projectOverview.id)) {
          return false;
        }

        const dryingProjectOverview = projectOverview as DryingProjectOverview;
        const estimatedDryingDurationInDays = dryingProjectOverview.estimatedDryingDurationInDays ??
          dryingProjectOverview.dryingProjectSteps?.damageAssessmentResult?.estimatedDryingTime;

        if (!estimatedDryingDurationInDays) {
          return false;
        }

        if (!dryingProjectOverview.damagePatternConclusionComplete) {
          return false;
        }

        if (
          dryingProjectOverview.damagePatternConclusionState !==
          DamagePatternConclusionState.CARRY_OUT_DRYING
        ) {
          return false;
        }

        if (dryingProjectOverview.dryingEnd) {
          return false;
        }

        return calculateDryingProgressPercent(dryingProjectOverview) > 1;
      }

      case ProjectStateFilterType.RECENTLY_FINISHED_RELEVANT_PROJECTS:
      case ProjectStateFilterType.RECENTLY_FINISHED: {
        return isProjectFinished(projectOverview);
      }
    }
  }
}
